import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import workbenchImage from "@/assets/images/sqithome.png";
import omics_station from "@/assets/images/omics_station.png";
import omics_simplified from "@/assets/images/omics_simplified.png";
import sqit_installed from "@/assets/images/sqit_installed.png";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { HashLink } from "react-router-hash-link";
import "swiper/css";
import "swiper/css/pagination";

const icon = (name, props = {}) => {
  const key = name.split("-").map((part) => part[0]?.toUpperCase() + part.slice(1)).join("");
  const Icon = Icons[key] || Icons.Circle;
  return <Icon {...props} />;
};

const details = [
  { title: "Software Infrastructure", icon: "server", items: ["High Performance Computing (HPC) Support", "Multi-Core Parallel Processing", "Linux Server Deployment", "Workflow Automation", "Secure Data Management", "Integrated Biological Databases", "Enterprise User Management"] },
  { title: "System Requirements", icon: "monitor-cog", items: ["Ubuntu 20.04 LTS or newer", "8+ CPU cores recommended", "32 GB RAM", "1 TB SSD storage", "Docker or Singularity support", "Workstation, server, or HPC cluster"] },
  { title: "SQIT Installed Across India", icon: "map-pinned", items: ["North India", "West India", "South India", "East India", "Central India"] },
  { title: "We Are With You Every Step", icon: "headphones", items: ["Installation & Configuration", "Training & Documentation", "Workflow Development", "Technical Support", "Annual Maintenance", "Pipeline Customization"] },
];

const organizations = ["Research Laboratories", "Universities & Institutions", "Hospitals & Medical Centers", "Biotechnology Companies", "Agri-genomics Labs", "Clinical Research Organizations", "Government Institutes", "Pharmaceutical Companies"];

function getBlock(page, type) {
  return (page?.sections || []).flatMap((section) => section.blocks || []).find((block) => block.type === type)?.data || {};
}

function SectionTitle({ children }) {
  return <h2 className="text-center text-base font-bold text-[#0B6936] sm:text-lg">{children}</h2>;
}

function TinyCard({ item }) {
  return (
    <div className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl">

      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF8EA] text-[#0B6936] transition-colors group-hover:bg-[#0B6936] group-hover:text-white">
        {icon(item.icon || "circle", { size: 26 })}
      </div>

      <h3 className="mt-6 text-xl font-semibold text-gray-900">
        {item.title}
      </h3>

      <p className="mt-4 text-base leading-7 text-gray-600">
        {item.text}
      </p>

    </div>
  );
}

function StatsGrid({ stats = [] }) {
  if (!stats.length) return null;

  return (
    <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
      {stats.map((stat, index) => (
        <div
          key={`${stat.label}-${index}`}
          className="rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm"
        >
          <div className="flex justify-center text-[#0B6936]">
            {icon(stat.icon || "circle", { size: 22 })}
          </div>

          <div className="mt-3 text-3xl font-bold text-[#0B6936]">
            {stat.value}
          </div>

          <div className="mt-1 text-sm text-gray-600">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SoftwareHomePage({ page }) {
  const hero = getBlock(page, "hero_v2") || getBlock(page, "hero");
  const overview = getBlock(page, "richtext");
  const omicsStation = getBlock(page, "omics_station");
  const sqitSoftware = getBlock(page, "sqit_software");
  const sqitSaas = getBlock(page, "sqit_saas");
  const whyChoose = getBlock(page, "why_choose");
  const supportedModules = getBlock(page, "supported_modules");
  const workflowData = getBlock(page, "workflow_v2");
  const workflow = workflowData.steps || [];
  const infrastructure = getBlock(page, "infrastructure_info");
  const infrastructureCards = infrastructure.cards || [];
  const infoCards = getBlock(page, "info_cards");
  const cards = infoCards.cards || [];
  const cta = getBlock(page, "cta_v2");
  const features = whyChoose.cards || [];
  const modules = supportedModules.modules || [];
  const ecosystem = getBlock(page, "ecosystem_overview");


  return (
    <main className="bg-[#FEFEFD] text-gray-900">

      <section
        className="py-10 sm:py-12 lg:py-16"
        style={{
          background:
            "linear-gradient(90deg, #F8FCEB 0%, #FFF7DA 50%, #F4FBF5 100%)",
        }}
      >
        <div className="sqit-container">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">

            {/* LEFT CONTENT */}
            <div>

              <span className="inline-flex rounded-full bg-[#EEF8EA] px-3 py-1 text-sm font-semibold uppercase tracking-wide text-[#0B6936]">
                {ecosystem.badge}
              </span>

              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">
                <span className="text-[#0B6936]">
                  {ecosystem.title}
                </span>
              </h2>

              <h3 className="mt-2 text-2xl font-semibold text-gray-900">
                {ecosystem.subtitle}
              </h3>

              <div
                className="mt-5 max-w-xl text-base leading-7 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: ecosystem.description || "",
                }}
              />

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {(ecosystem.highlights || []).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3"
                  >
                    <Icons.CircleCheck
                      size={18}
                      className="text-[#0B6936] shrink-0"
                    />
                    <span className="font-medium text-gray-700">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

            </div>

            {/* RIGHT IMAGE */}
            <div className="overflow-hidden rounded-2xl">
              <div className="flex h-[260px] items-center justify-center p-4 sm:h-[340px] lg:h-[460px]">
                {(ecosystem.images || []).length > 0 ? (
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    slidesPerView={1}
                    loop
                    autoplay={{
                      delay: 5000,
                      disableOnInteraction: false,
                    }}
                    pagination={{ clickable: true }}
                    className="h-[260px] w-full sm:h-[340px] lg:h-[460px]"
                  >
                    {ecosystem.images.map((item, index) => (
                      <SwiperSlide key={index}>
                        <div className="flex h-full w-full items-center justify-center p-4">
                          <img
                            src={item.image}
                            alt={item.caption || `Image ${index + 1}`}
                            className="h-auto w-auto max-h-[95%] max-w-[95%] object-contain"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <img
                    src={workbenchImage}
                    alt={ecosystem.title}
                    className="h-auto w-auto max-h-[95%] max-w-[95%] object-contain"
                  />
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
      <section id="omics-station"
        className="scroll-mt-8 py-16"
        style={{
          background: "#FFFDF3",
        }}
      >
        <div className="sqit-container">
          <div className="grid gap-12 lg:grid-cols-[52%_48%] lg:items-center">

            {/* LEFT : Product Image */}
            <div className="relative">
              <img
                src={omicsStation.image || omics_station}
                alt={omicsStation.title || "Omics Station"}
                className="w-full max-w-none h-auto object-contain"
              />
            </div>

            {/* RIGHT : Content */}
            <div>

              {/* Badge */}
              <span className="inline-flex rounded-full bg-[#EEF8EA] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#0B6936]">
                {omicsStation.badge}
              </span>

              {/* Heading */}
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">
                <span className="text-[#0B6936]">
                  {omicsStation.title || "Omics Station"}
                </span>
              </h2>

              <h3 className="mt-2 text-2xl font-semibold text-gray-900">
                {omicsStation.subtitle}
              </h3>

              {/* Description */}
              <p className="mt-5 text-gray-600 leading-7">
                {omicsStation.description}
              </p>

              {/* Feature Cards */}
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {(omicsStation.features || []).map((feature, index) => (
                  <div
                    key={`${feature.title}-${index}`}
                    className="rounded-xl border p-4"
                  >
                    {icon(feature.icon || "circle", {
                      size: 22,
                      className: "text-[#0B6936]",
                    })}

                    <h4 className="mt-3 font-semibold">
                      {feature.title}
                    </h4>

                    <p className="mt-2 text-sm text-gray-600">
                      {feature.text}
                    </p>
                  </div>
                ))}
              </div>

              <StatsGrid stats={omicsStation.stats} />

              {/* Buttons */}
              <div className="mt-8 flex">
                <Link
                  to={omicsStation.button?.href || "/documentation"}
                  className="inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#09592d] sm:px-5 sm:py-3"
                >
                  {omicsStation.button?.label || "Explore Omics Station"}
                  <Icons.ArrowRight size={16} />
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      <section id="sqit-saas"
        className="scroll-mt-8 py-16 bg-white"
      >
        <div className="sqit-container">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">

            {/* LEFT CONTENT */}
            <div>

              <span className="inline-flex rounded-full bg-[#EEF8EA] px-3 py-1 text-sm font-semibold uppercase tracking-wide text-[#0B6936]">
                {sqitSaas.badge}
              </span>

              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">
                <span className="text-gray-900">
                  {sqitSaas.title?.split(" ")[0] || "SQIT"}
                </span>{" "}
                <span className="text-[#0B6936]">
                  {sqitSaas.title?.split(" ").slice(1).join(" ") || "Software"}
                </span>
              </h2>

              <h3 className="mt-2 text-2xl font-semibold text-gray-900">
                {sqitSaas.subtitle || "A Complete Bioinformatics Workbench"}
              </h3>

              <div
                className="mt-5 max-w-xl text-base leading-7 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: sqitSaas.description || ""
                }}
              />

              <Link
                to={sqitSaas.button?.href || "/login"}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-5 py-3 text-sm font-semibold text-white hover:bg-[#09572c]"
              >
                {sqitSaas.button?.label || "Explore SQIT Software"}
                <Icons.ArrowRight size={16} />
              </Link>

              {/* Features */}
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                {(sqitSaas.features || []).map((feature, index) => (
                  <div
                    key={`${feature.title}-${index}`}
                    className="flex gap-3"
                  >
                    {icon(feature.icon || "circle", {
                      size: 30,
                      className: "mt-1 text-[#0B6936]",
                    })}

                    <div>
                      <h4 className="font-semibold">
                        {feature.title}
                      </h4>

                      <p className="mt-1 text-sm text-gray-600">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>



            </div>

            {/* RIGHT IMAGE */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
              <img
                src={sqitSaas.image || workbenchImage}
                alt={sqitSaas.title || "SQIT Software"}
                className="w-full h-auto object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      <section id="sqit-ai"
        className="scroll-mt-6 py-16"
        style={{
          background:
            "linear-gradient(90deg, #F8FCEB 0%, #FFF7DA 50%, #F4FBF5 100%)",
        }}
      >
        <div className="sqit-container">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">


            {/* LEFT IMAGE */}
            <div className="overflow-hidden rounded-2xl">
              <div className="flex h-[280px] items-center justify-center sm:h-[380px] lg:h-[520px]">
                {(sqitSoftware.images || []).length > 0 ? (
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    slidesPerView={1}
                    loop
                    autoplay={{
                      delay: 5000,
                      disableOnInteraction: false,
                    }}
                    pagination={{ clickable: true }}
                    className="h-[280px] w-full sm:h-[380px] lg:h-[520px]"
                  >
                    {sqitSoftware.images.map((item, index) => (
                      <SwiperSlide key={index}>
                        <div className="flex h-full w-full items-center justify-center">
                          <img
                            src={item.image}
                            alt={item.caption || `Screenshot ${index + 1}`}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <img
                    src={workbenchImage}
                    alt={sqitSoftware.title}
                    className="h-full w-full object-contain"
                  />
                )}
              </div>
            </div>

            {/* RIGHT CONTENT */}
            <div>

              <span className="inline-flex rounded-full bg-[#EEF8EA] px-3 py-1 text-sm font-semibold uppercase tracking-wide text-[#0B6936]">
                {sqitSoftware.badge}
              </span>

              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">
                <span className="text-gray-900">
                  {sqitSoftware.title?.split(" ")[0] || "SQIT"}
                </span>{" "}
                <span className="text-[#0B6936]">
                  {sqitSoftware.title?.split(" ").slice(1).join(" ") || "Software"}
                </span>
              </h2>

              <h3 className="mt-2 text-2xl font-semibold text-gray-900">
                {sqitSoftware.subtitle || "A Complete Bioinformatics Workbench"}
              </h3>

              <div
                className="mt-5 max-w-xl text-base leading-7 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: sqitSoftware.description || ""
                }}
              />

              {/* Features */}
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                {(sqitSoftware.features || []).map((feature, index) => (
                  <div
                    key={`${feature.title}-${index}`}
                    className="flex gap-3"
                  >
                    {icon(feature.icon || "circle", {
                      size: 20,
                      className: "mt-1 text-[#0B6936]",
                    })}

                    <div>
                      <h4 className="font-semibold">
                        {feature.title}
                      </h4>

                      <p className="mt-1 text-sm text-gray-600">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <StatsGrid stats={sqitSoftware.stats} />

              <Link
                to={sqitSoftware.button?.href || "/documentation"}
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-5 py-3 text-sm font-semibold text-white hover:bg-[#09572c]"
              >
                {sqitSoftware.button?.label || "Explore SQIT Software"}
                <Icons.ArrowRight size={16} />
              </Link>

            </div>



          </div>
        </div>
      </section>

      <section
        className="py-10 sm:py-12 lg:py-14"
        style={{
          background: "#F8FCEB",
        }}
      >
        <div className="sqit-container">

          <h2 className="text-center text-3xl font-bold">
            {whyChoose.title || "Why Researchers Choose SQIT"}
          </h2>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {features.map((item, index) => (
              <TinyCard
                key={`${item.title}-${index}`}
                item={item}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        id="supported-modules"
        className="scroll-mt-24 py-10 sm:py-12 lg:py-14"
        style={{
          background: "#FFFDF3",
        }}
      >
        <div className="sqit-container">

          <h2 className="text-center text-3xl font-bold">
            {supportedModules.title || "Supported Analysis Modules"}
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">

            {modules
              .filter((item) => item.display_in?.public ?? true)
              .map((item, index) => (
                <div
                  key={`${item.title}-${index}`}
                  className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF8EA] text-[#0B6936] transition-colors group-hover:bg-[#0B6936] group-hover:text-white">
                    {icon(item.icon || "circle", { size: 20 })}
                  </div>

                  <h3 className="mt-5 text-2xl font-bold leading-tight text-gray-900 lg:text-[32px]">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-base leading-7 text-gray-700 lg:text-[20px] lg:leading-9">
                    {item.description || item.text}
                  </p>

                  {item.show_sample_report && item.sample_report && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      <Link
                        to={`/sample-reports/${item.sample_report}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-md border border-[#0B6936] px-4 py-2 text-sm font-semibold text-[#0B6936] transition hover:bg-[#0B6936] hover:text-white"
                      >
                        View Report
                        <Icons.ArrowRight size={16} />
                      </Link>
                      <a
                        href={`/sample-reports/${item.sample_report}.html`}
                        download={`${item.sample_report}.html`}
                        className="inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#09572c]"
                      >
                        Download
                      </a>
                    </div>
                  )}
                </div>
              ))}

          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/modules"
              className="inline-flex items-center gap-2 text-base font-semibold text-[#0B6936] transition hover:text-[#09572c] hover:underline"
            >
              View All Modules
              <Icons.ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section
        className="py-14 bg-white"
      >
        <div className="sqit-container">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 shadow-sm">

            <h2 className="text-center text-3xl font-bold">
              {workflowData.title || "How SQIT Works"}
            </h2>

            <div className="mt-10 grid gap-5 sm:p-6 lg:p-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">

              {workflow.map((step, index) => (
                <div key={step.number} className="relative text-center">

                  {/* Arrow */}
                  {index < workflow.length - 1 && (
                    <div className="absolute left-[calc(100%-12px)] top-5 sm:p-6 lg:p-8 hidden w-16 items-center justify-center lg:flex">
                      <Icons.ArrowRight
                        size={34}
                        className="text-[#0B6936]/70"
                      />
                    </div>
                  )}

                  {/* Icon */}
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-[#EEF8EA] text-[#0B6936] transition-all duration-300 hover:bg-[#0B6936] hover:text-white">
                    {icon(
                      step.icon || "circle",
                      { size: 28 }
                    )}
                  </div>

                  {/* Step Number */}
                  <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#0B6936]">
                    Step {step.number}
                  </div>

                  {/* Title */}
                  <h3 className="mt-2 text-lg font-semibold text-gray-900">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    {step.text}
                  </p>

                </div>
              ))}

            </div>

          </div>
        </div>
      </section>

      <section
        className="py-10 sm:py-12 lg:py-14"
        style={{
          background: "#F8FCEB",
        }}
      >
        <div className="sqit-container">
          <div className="grid gap-5 sm:p-6 lg:p-8 lg:grid-cols-[1fr_0.9fr]">

            {/* LEFT COLUMN */}
            <div className="flex flex-col gap-5 sm:p-6 lg:p-8">

              {/* Software Infrastructure */}
              {infrastructureCards[0] && (
                <div className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl">
                  <div className="flex items-center gap-4 text-[#0B6936]">
                    {icon(infrastructureCards[0].icon || "server", { size: 30 })}
                    <h3 className="text-2xl font-semibold">
                      {infrastructureCards[0].title}
                    </h3>
                  </div>

                  <ul className="mt-5 space-y-3">
                    {(infrastructureCards[0].items || []).map((item, index) => (
                      <li key={index} className="flex gap-2 text-base leading-7 text-gray-700">
                        <Icons.CircleCheck size={16} className="mt-1 shrink-0 text-[#0B6936]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* System Requirements */}
              {infrastructureCards[1] && (
                <div className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl">
                  <div className="flex items-center gap-4 text-[#0B6936]">
                    {icon(infrastructureCards[1].icon || "monitor-cog", { size: 30 })}
                    <h3 className="text-2xl font-semibold">
                      {infrastructureCards[1].title}
                    </h3>
                  </div>

                  <ul className="mt-5 space-y-3">
                    {(infrastructureCards[1].items || []).map((item, index) => (
                      <li key={index} className="flex gap-2 text-base leading-7 text-gray-700">
                        <Icons.CircleCheck size={16} className="mt-1 shrink-0 text-[#0B6936]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Support */}
              {infrastructureCards[3] && (
                <div className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl">
                  <div className="flex items-center gap-4 text-[#0B6936]">
                    {icon(infrastructureCards[3].icon || "headphones", { size: 30 })}
                    <h3 className="text-2xl font-semibold">
                      {infrastructureCards[3].title}
                    </h3>
                  </div>

                  <ul className="mt-5 space-y-3">
                    {(infrastructureCards[3].items || []).map((item, index) => (
                      <li key={index} className="flex gap-2 text-base leading-7 text-gray-700">
                        <Icons.CircleCheck size={16} className="mt-1 shrink-0 text-[#0B6936]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>

            {/* RIGHT COLUMN */}
            {infrastructureCards[2] && (
              <div className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl">

                <div className="flex items-center gap-4 text-[#0B6936]">
                  {icon(infrastructureCards[2].icon || "map-pinned", { size: 30 })}
                  <h3 className="text-2xl font-semibold">
                    {infrastructureCards[2].title}
                  </h3>
                </div>

                <div className="mt-6 flex flex-1 items-center justify-center">
                  <img
                    src={infrastructureCards[2].image || sqit_installed}
                    alt={infrastructureCards[2].title}
                    className="h-full w-full object-contain"
                  />
                </div>

              </div>
            )}

          </div>
        </div>
      </section>

      <section
        className="py-20"
        style={{
          background: "#FFFDF3",
        }}
      >
        <div className="sqit-container">
          <div className="grid gap-5 sm:p-6 lg:p-8 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card, index) => (
              <article
                key={index}
                className="group rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#0B6936]/20 hover:shadow-xl"
              >
                <div className="flex items-center gap-4">

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF8EA] text-[#0B6936] transition-colors group-hover:bg-[#0B6936] group-hover:text-white">
                    {icon(card.icon || "file-text", { size: 28 })}
                  </div>

                  <h3 className="text-2xl font-semibold text-gray-900">
                    {card.title}
                  </h3>

                </div>

                {card.description && (
                  <p className="mt-3 text-base leading-7 text-gray-700">
                    {card.description}
                  </p>
                )}

                {(card.links || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
                    {card.links.map((item, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1"
                      >
                        <Icons.FileText
                          size={13}
                          className="text-[#0B6936]"
                        />
                        {item}
                      </span>
                    ))}
                  </div>
                )}

                {card.button?.label &&
                  (card.title === "Sample Reports" ? (
                    <HashLink
                      smooth
                      to="#supported-modules"
                      className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#006B3C] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#005A33]"
                    >
                      {card.button.label}
                      <Icons.ArrowRight size={14} />
                    </HashLink>
                  ) : (
                    <Link
                      to={card.button.href || "/"}
                      className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#006B3C] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#005A33]"
                    >
                      {card.button.label}
                      <Icons.ArrowRight size={14} />
                    </Link>
                  ))}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sqit-container pb-10">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#067958] via-[#178362] to-[#44997E] px-6 py-8 text-white lg:px-10 lg:py-10">

          <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,#b4e39d_1px,transparent_1px)] [background-size:22px_22px]" />

          <div className="relative flex flex-col gap-5 sm:p-6 lg:p-8 lg:flex-row lg:items-center lg:justify-between">

            {/* Left Content */}
            <div className="max-w-4xl">
              <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl">
                {cta.title || "Ready to Transform Your Bioinformatics Research?"}
              </h2>

              <p className="mt-4 max-w-3xl text-base leading-7 text-white/85">
                {cta.description}
              </p>
            </div>

            {/* Right Button */}
            <div className="shrink-0">
              <Link
                to={cta.button?.href || "/contact"}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-base font-semibold text-[#07522f] transition hover:bg-gray-100"
              >
                {cta.button?.label || "Request a Demo"}
                <Icons.ArrowRight size={18} />
              </Link>
            </div>

          </div>

        </div>
      </section>
    </main>
  );
}
